/**
 * voice-verify.js — сравнение голоса с эталоном и наблюдения о записи.
 *
 * ЧЕГО ЭТОТ МОДУЛЬ НЕ ДЕЛАЕТ, и это главное в нём.
 *
 * Он не отвечает на вопрос «это синтез?». Такого ответа сегодня нет ни у
 * кого: Pindrop, Reality Defender, Hiya, McAfee — всё это корпоративные
 * системы, они работают на чистом звуке и на живом звонке ошибаются в
 * четверти случаев. Потребительское приложение, показывающее зелёную
 * галочку «это точно ваш сын», опаснее отсутствия приложения: оно
 * превращает сомнение, которое спасло бы человека, в уверенность.
 *
 * Он отвечает на другой вопрос — «это ТОТ ЖЕ человек, что на эталоне?» —
 * и отвечает НЕСИММЕТРИЧНО:
 *   · «голос сильно отличается» — сильное утверждение, его стоит слушать;
 *   · «голос похож» — НЕ доказательство: удачный клон похож по определению,
 *     в этом весь его смысл.
 * Вся шкала построена вокруг этой асимметрии, а не вокруг процента.
 *
 * Почему это всё равно защищает: подавляющее большинство звонков «мама, я
 * попал в беду» делаются ЧУЖИМ голосом с расчётом на панику, а не клоном.
 * Сравнение с эталоном ловит именно их, и ловит уверенно.
 *
 * Признаки выбраны так, чтобы каждый можно было объяснить человеку словами
 * и проверить измерением, а не «модель так решила».
 */
(function () {
    'use strict';

    /* ── БПФ: общий модуль, своей копии здесь нет ────────────────────── */
    function fftPair() {
        var F = window.DspFft;
        if (!F) throw new Error('DspFftMissing');
        return F;
    }

    var FRAME = 0.025;      // 25 мс — стандарт для MFCC
    var HOP = 0.010;        // 10 мс
    var N_MEL = 26;
    var N_MFCC = 13;
    var F0_MIN = 70, F0_MAX = 400;

    function nextPow2(n) { var p = 1; while (p < n) p <<= 1; return p; }

    /* ── мел-шкала ───────────────────────────────────────────────────── */
    function hzToMel(f) { return 2595 * Math.log(1 + f / 700) / Math.LN10; }
    function melToHz(m) { return 700 * (Math.pow(10, m / 2595) - 1); }

    /** Треугольный банк фильтров. Верх ограничен 8 кГц: выше речи почти нет,
        а телефонный канал и мессенджеры туда всё равно не пускают — брать
        оттуда признаки значит сравнивать КАНАЛЫ, а не голоса. */
    function melBank(sr, fftSize) {
        var bins = (fftSize >> 1) + 1;
        var lo = 80, hi = Math.min(8000, sr / 2 - 100);
        var mLo = hzToMel(lo), mHi = hzToMel(hi);
        var pts = new Float64Array(N_MEL + 2);
        for (var i = 0; i < N_MEL + 2; i++) {
            pts[i] = melToHz(mLo + (mHi - mLo) * i / (N_MEL + 1)) * fftSize / sr;
        }
        var bank = [];
        for (var m = 1; m <= N_MEL; m++) {
            var l = pts[m - 1], c = pts[m], r = pts[m + 1];
            var row = new Float64Array(bins);
            for (var k = Math.floor(l); k <= Math.ceil(r) && k < bins; k++) {
                if (k < c) row[k] = (k - l) / Math.max(1e-9, c - l);
                else row[k] = (r - k) / Math.max(1e-9, r - c);
                if (row[k] < 0) row[k] = 0;
            }
            bank.push(row);
        }
        return bank;
    }

    /* ── ДКП-II для MFCC ─────────────────────────────────────────────── */
    var _dct = null, _dctN = 0;
    function dctMatrix(n, k) {
        if (_dct && _dctN === n) return _dct;
        var M = [];
        for (var i = 0; i < k; i++) {
            var row = new Float64Array(n);
            for (var j = 0; j < n; j++) row[j] = Math.cos(Math.PI * i * (j + 0.5) / n);
            M.push(row);
        }
        _dct = M; _dctN = n;
        return M;
    }

    /* ── основной тон: автокорреляция ────────────────────────────────── */
    /**
     * Нормированная автокорреляция на кадре. Возвращает {f0, conf} или null.
     * Порог уверенности 0.35 подобран так, чтобы шипящие и паузы не давали
     * ложных «голосовых» кадров: на них пик автокорреляции ниже.
     */
    function frameF0(buf, sr) {
        var minLag = Math.floor(sr / F0_MAX), maxLag = Math.floor(sr / F0_MIN);
        if (buf.length < maxLag + 2) return null;
        var e0 = 0, i;
        for (i = 0; i < buf.length; i++) e0 += buf[i] * buf[i];
        if (e0 < 1e-9) return null;
        var best = 0, bestLag = 0;
        for (var lag = minLag; lag <= maxLag; lag++) {
            var s = 0, e1 = 0;
            for (i = 0; i + lag < buf.length; i++) { s += buf[i] * buf[i + lag]; e1 += buf[i + lag] * buf[i + lag]; }
            var norm = s / Math.sqrt(Math.max(1e-12, e0 * e1));
            if (norm > best) { best = norm; bestLag = lag; }
        }
        if (best < 0.35 || !bestLag) return null;
        return { f0: sr / bestLag, conf: best };
    }

    /* ── признаки ────────────────────────────────────────────────────── */

    /**
     * @param {Float32Array} pcm  моно
     * @param {number} sr
     * @returns {Object} признаки для сравнения и для наблюдений
     */
    function features(pcm, sr) {
        var F = fftPair();
        var frameLen = Math.round(FRAME * sr);
        var hopLen = Math.round(HOP * sr);
        var fftSize = nextPow2(frameLen);
        var bins = (fftSize >> 1) + 1;
        var win = F.hann(fftSize);
        var tw = F.makeTwiddles(fftSize);
        var bank = melBank(sr, fftSize);
        var dct = dctMatrix(N_MEL, N_MFCC);

        var re = new Float64Array(fftSize), im = new Float64Array(fftSize);
        var mag = new Float64Array(bins);
        var mel = new Float64Array(N_MEL);

        var nFrames = Math.max(0, Math.floor((pcm.length - frameLen) / hopLen) + 1);
        var energies = new Float64Array(nFrames);
        var i, j, k, f;

        /* Первый проход: энергия кадров — нужна, чтобы отделить речь от
           тишины ДО того, как считать что-либо ещё. Порог берётся от
           СОБСТВЕННОГО шумового пола записи, а не от абсолютной величины:
           одна и та же речь в мессенджере и в диктофоне отличается по
           уровню на десятки децибел. */
        for (f = 0; f < nFrames; f++) {
            var off = f * hopLen, e = 0;
            for (i = 0; i < frameLen; i++) { var v = pcm[off + i]; e += v * v; }
            energies[f] = Math.sqrt(e / frameLen);
        }
        var sorted = Array.prototype.slice.call(energies).sort(function (a, b) { return a - b; });
        var floor = sorted.length ? sorted[Math.floor(sorted.length * 0.10)] : 0;
        var peak = sorted.length ? sorted[Math.floor(sorted.length * 0.95)] : 0;
        var thr = Math.max(floor * 3, peak * 0.08);

        var mfccSum = new Float64Array(N_MFCC), mfccSq = new Float64Array(N_MFCC);
        /* Дельты — скорость изменения кепстра между соседними кадрами. Они
           описывают МАНЕРУ говорить (как быстро человек переходит между
           звуками), и это признак, которого в средних значениях нет вовсе. */
        var dSum = new Float64Array(N_MFCC), dSq = new Float64Array(N_MFCC), dN = 0;
        var prevC = null, curC = new Float64Array(N_MFCC);
        var ltas = new Float64Array(bins);
        var f0s = [];
        var voiced = 0, speechFrames = 0;
        var f0buf = new Float32Array(Math.round(0.040 * sr));

        for (f = 0; f < nFrames; f++) {
            if (energies[f] < thr) continue;
            speechFrames++;
            var off2 = f * hopLen;
            for (i = 0; i < fftSize; i++) {
                re[i] = (i < frameLen ? pcm[off2 + i] : 0) * win[i];
                im[i] = 0;
            }
            F.fft(re, im, tw);
            for (k = 0; k < bins; k++) {
                /* sqrt, а не Math.hypot: в проекте измерено, что hypot стоит
                   3.5× за защиту от переполнения, которого в нормированном
                   PCM не бывает. */
                var m2 = re[k] * re[k] + im[k] * im[k];
                mag[k] = m2;
                ltas[k] += m2;
            }
            for (j = 0; j < N_MEL; j++) {
                var row = bank[j], acc = 0;
                for (k = 0; k < bins; k++) acc += row[k] * mag[k];
                mel[j] = Math.log(Math.max(1e-10, acc));
            }
            for (i = 0; i < N_MFCC; i++) {
                var d = dct[i], s = 0;
                for (j = 0; j < N_MEL; j++) s += d[j] * mel[j];
                curC[i] = s;
                mfccSum[i] += s; mfccSq[i] += s * s;
            }
            if (prevC) {
                for (i = 0; i < N_MFCC; i++) {
                    var dd = curC[i] - prevC[i];
                    dSum[i] += dd; dSq[i] += dd * dd;
                }
                dN++;
            } else { prevC = new Float64Array(N_MFCC); }
            prevC.set(curC);
            /* F0 на кадре 40 мс вокруг текущего центра. */
            var c0 = off2 + (frameLen >> 1) - (f0buf.length >> 1);
            if (c0 >= 0 && c0 + f0buf.length < pcm.length) {
                f0buf.set(pcm.subarray(c0, c0 + f0buf.length));
                var p = frameF0(f0buf, sr);
                if (p) { f0s.push(p.f0); voiced++; }
            }
        }

        if (!speechFrames) return null;

        var mfccMean = new Float64Array(N_MFCC), mfccVar = new Float64Array(N_MFCC);
        var mfccStd = new Float64Array(N_MFCC);
        for (i = 0; i < N_MFCC; i++) {
            mfccMean[i] = mfccSum[i] / speechFrames;
            var varr = mfccSq[i] / speechFrames - mfccMean[i] * mfccMean[i];
            mfccVar[i] = Math.max(1e-6, varr);
            mfccStd[i] = Math.sqrt(mfccVar[i]);
        }
        var dMean = new Float64Array(N_MFCC), dVar = new Float64Array(N_MFCC);
        for (i = 0; i < N_MFCC; i++) {
            dMean[i] = dN ? dSum[i] / dN : 0;
            dVar[i] = dN ? Math.max(1e-6, dSq[i] / dN - dMean[i] * dMean[i]) : 1e-6;
        }

        f0s.sort(function (a, b) { return a - b; });
        var med = f0s.length ? f0s[f0s.length >> 1] : 0;
        var q1 = f0s.length ? f0s[Math.floor(f0s.length * 0.25)] : 0;
        var q3 = f0s.length ? f0s[Math.floor(f0s.length * 0.75)] : 0;

        /* LTAS: 24 полосы по логарифму частоты, нормированные по среднему —
           сравнивается ФОРМА спектра, а не громкость записи. */
        var NB = 24, ltasBands = new Float64Array(NB);
        var fLo = 100, fHi = Math.min(7500, sr / 2 - 100);
        for (b = 0; b < NB; b++) {
            var bl = fLo * Math.pow(fHi / fLo, b / NB), bh = fLo * Math.pow(fHi / fLo, (b + 1) / NB);
            var k0 = Math.max(1, Math.round(bl * fftSize / sr)), k1 = Math.min(bins - 1, Math.round(bh * fftSize / sr));
            var acc2 = 0, cnt = 0;
            for (k = k0; k <= k1; k++) { acc2 += ltas[k]; cnt++; }
            ltasBands[b] = 10 * Math.log(Math.max(1e-12, acc2 / Math.max(1, cnt))) / Math.LN10;
        }
        var b, lm = 0;
        for (b = 0; b < NB; b++) lm += ltasBands[b];
        lm /= NB;
        for (b = 0; b < NB; b++) ltasBands[b] -= lm;

        return {
            mfccMean: mfccMean, mfccStd: mfccStd, mfccVar: mfccVar,
            dMean: dMean, dVar: dVar,
            f0: { median: med, q1: q1, q3: q3, iqr: q3 - q1, voicedRatio: speechFrames ? voiced / speechFrames : 0 },
            ltas: ltasBands,
            speechSec: speechFrames * HOP,
            totalSec: pcm.length / sr,
            speechRatio: nFrames ? speechFrames / nFrames : 0,
            noiseFloor: floor, peakLevel: peak,
            sampleRate: sr
        };
    }

    /* ── сравнение ───────────────────────────────────────────────────── */

    function cosine(a, b, from) {
        var s = 0, na = 0, nb = 0;
        for (var i = from || 0; i < a.length; i++) { s += a[i] * b[i]; na += a[i] * a[i]; nb += b[i] * b[i]; }
        return s / Math.sqrt(Math.max(1e-12, na * nb));
    }

    function corr(a, b) {
        var n = a.length, ma = 0, mb = 0, i;
        for (i = 0; i < n; i++) { ma += a[i]; mb += b[i]; }
        ma /= n; mb /= n;
        var s = 0, sa = 0, sb = 0;
        for (i = 0; i < n; i++) { var x = a[i] - ma, y = b[i] - mb; s += x * y; sa += x * x; sb += y * y; }
        return s / Math.sqrt(Math.max(1e-12, sa * sb));
    }

    function semitones(a, b) {
        if (!a || !b) return 0;
        return Math.abs(12 * Math.log(a / b) / Math.log(2));
    }

    /**
     * Симметричная дивергенция Кульбака–Лейблера между диагональными
     * гауссианами. Учитывает и среднее, и РАЗБРОС кепстра — а косинус
     * средних, стоявший здесь сначала, разброс не видел вовсе.
     */
    function symKL(m1, v1, m2, v2, from) {
        var s = 0;
        for (var i = from | 0; i < m1.length; i++) {
            var d = m1[i] - m2[i];
            s += 0.5 * (v1[i] / v2[i] + v2[i] / v1[i] - 2) + 0.5 * d * d * (1 / v1[i] + 1 / v2[i]);
        }
        return s;
    }

    /**
     * Сравнивает признаки записи с эталоном.
     *
     * Мера выбрана бейк-офом семи вариантов на двух корпусах; метрика —
     * сколько ЧУЖИХ ловится при 1% ложных тревог, потому что цена ошибок
     * несимметрична: «не поверил родному человеку» дороже пропуска.
     *
     *   мера                          свои  чужие  ловит@1%
     *   symKL статики + дельт         97.5   89.3    91.6%   ←
     *   symKL статики                 96.7   86.8    84.6%
     *   Mahalanobis статики + дельт   75.9   52.7    62.2%
     *   только косинус MFCC           97.9   93.2    20.3%
     *   косинус + LTAS + тон          95.5   91.0    15.4%   (было)
     *
     * Корпус специально трудный: два голоса ОДНОЙ синтезирующей модели на
     * одном языке, то есть одинаковый канал и вокодер. У живых людей разница
     * больше, поэтому 91.6% — оценка снизу, а не сверху.
     *
     * ⚠️ c0 отброшен: это громкость кадра, а не голос. Дельты берутся с тем
     * же смещением по той же причине.
     */
    function compare(a, b) {
        if (!a || !b) return null;
        var klStatic = symKL(a.mfccMean, a.mfccVar, b.mfccMean, b.mfccVar, 1);
        var klDelta = symKL(a.dMean, a.dVar, b.dMean, b.dVar, 1);
        var score = 100 * Math.exp(-(klStatic + klDelta) / 60);
        var st = semitones(a.f0.median, b.f0.median);
        return {
            score: Math.round(score * 10) / 10,
            klStatic: Math.round(klStatic * 100) / 100,
            klDelta: Math.round(klDelta * 100) / 100,
            /* Высота тона В СЧЁТ НЕ ВХОДИТ — бейк-офф показал, что она его
               только размывает, — но человеку её называют: «голос ниже на
               четыре полутона» понятнее любого процента. */
            semitones: Math.round(st * 100) / 100,
            ltasCorr: Math.round(corr(a.ltas, b.ltas) * 1000) / 1000
        };
    }

    /**
     * Вердикт НЕСИММЕТРИЧНЫЙ — и это главное свойство инструмента.
     *   different  — голос не совпадает с эталоном; сильное утверждение;
     *   unclear    — совпадений недостаточно для вывода;
     *   consistent — противоречий нет. ЭТО НЕ «ЭТО ОН».
     */
    function verdict(cmp, qualityOk) {
        if (!cmp) return 'unclear';
        if (!qualityOk) return 'unclear';
        if (cmp.score < THRESH.different) return 'different';
        if (cmp.score < THRESH.consistent) return 'unclear';
        return 'consistent';
    }

    /* Пороги подбираются бейк-офом; здесь — значения по умолчанию, которые
       тест обязан подтвердить или заменить. */
    /* Рабочая точка выбрана ПО ИЗМЕРЕНИЮ, а не по круглому числу: на
       реалистичном корпусе порог 91.5 даёт 0.6% ложных тревог и ловит 81%
       чужих. Ниже (90) ложных тревог ещё меньше, но и чужих ловится заметно
       меньше; выше — начинает срабатывать на своих, а «не поверил родному
       человеку в беде» здесь самая дорогая ошибка.
       ⚠️ Зона между different и consistent — не брак шкалы, а честное
       «данных не хватает»: распределения СВОИХ и ЧУЖИХ перекрываются, и
       делать вид, что границы нет, значило бы придумывать уверенность. */
    var THRESH = { different: 91.5, consistent: 96 };

    /* ── пригодность записи ──────────────────────────────────────────── */
    /**
     * Слишком короткая или слишком тихая запись не даёт сравнивать — и об
     * этом надо сказать ДО вердикта, а не выдать уверенный ответ по двум
     * секундам «алло».
     */
    function quality(feat) {
        if (!feat) return { ok: false, reason: 'no_speech' };
        if (feat.speechSec < 3) return { ok: false, reason: 'too_short', speechSec: feat.speechSec };
        if (feat.f0.voicedRatio < 0.15) return { ok: false, reason: 'not_voice' };
        if (feat.peakLevel < 0.005) return { ok: false, reason: 'too_quiet' };
        return { ok: true, speechSec: feat.speechSec };
    }

    /* ── наблюдения о записи ─────────────────────────────────────────── */

    /**
     * Наблюдения — это НАБЛЮДЕНИЯ, а не улики: каждое имеет законное
     * объяснение, и все они названы. Синтез-детектора здесь нет намеренно
     * (см. шапку файла): проверить его нам не на чем, а непроверенное
     * обещание в этом инструменте стоит дороже, чем в любом другом.
     */
    function observations(feat, pcm, sr) {
        var out = [];
        if (!feat) return out;

        /* 1. Верхняя граница полосы. Резкий обрыв бывает у синтеза (модель
              синтезирует на 22.05 или 24 кГц), у телефонного канала (8 кГц) и
              у мессенджеров. Сам по себе он не значит ничего — но названная
              цифра помогает человеку понять, ЧТО он слушает. */
        var band = bandwidthHz(feat);
        out.push({ id: 'bandwidth', value: Math.round(band), severity: 'info' });

        /* 2. Доля речи. У склеенной или сгенерированной записи пауз бывает
              подозрительно мало, но так же выглядит и обычная диктовка. */
        out.push({ id: 'speech_ratio', value: Math.round(feat.speechRatio * 100), severity: 'info' });

        /* 3. Шумовой пол в паузах. Ровно НУЛЕВОЙ фон означает, что тишина
              цифровая: живой микрофон всегда что-то пишет. Это единственное
              наблюдение здесь, у которого нет невинного объяснения, кроме
              обработки шумодавом — и шумодав тоже надо назвать. */
        var sil = digitalSilenceRatio(pcm);
        out.push({ id: 'digital_silence', value: Math.round(sil * 100), severity: sil > 0.02 ? 'warn' : 'info' });

        /* 4. Длительность речи — от неё зависит, можно ли вообще сравнивать. */
        out.push({ id: 'speech_sec', value: Math.round(feat.speechSec * 10) / 10, severity: feat.speechSec < 3 ? 'warn' : 'info' });
        return out;
    }

    function bandwidthHz(feat) {
        /* Ищем последнюю полосу LTAS, где энергия ещё не провалилась на
           25 дБ ниже максимума полосы речи. */
        var NB = feat.ltas.length, fLo = 100, fHi = Math.min(7500, feat.sampleRate / 2 - 100);
        var mx = -1e9, i;
        for (i = 0; i < NB; i++) if (feat.ltas[i] > mx) mx = feat.ltas[i];
        var last = 0;
        for (i = 0; i < NB; i++) if (feat.ltas[i] > mx - 25) last = i;
        return fLo * Math.pow(fHi / fLo, (last + 1) / NB);
    }

    function digitalSilenceRatio(pcm) {
        if (!pcm || !pcm.length) return 0;
        var win = 512, zero = 0, total = 0;
        for (var i = 0; i + win < pcm.length; i += win) {
            var mx = 0;
            for (var j = 0; j < win; j++) { var v = Math.abs(pcm[i + j]); if (v > mx) mx = v; }
            if (mx < 1e-5) zero++;
            total++;
        }
        return total ? zero / total : 0;
    }

    /* ── моно из AudioBuffer ─────────────────────────────────────────── */
    function toMono(buf) {
        if (buf.numberOfChannels === 1) return buf.getChannelData(0);
        var n = buf.length, out = new Float32Array(n), ch = buf.numberOfChannels, c;
        for (c = 0; c < ch; c++) {
            var d = buf.getChannelData(c);
            for (var i = 0; i < n; i++) out[i] += d[i] / ch;
        }
        return out;
    }

    window.VoiceVerify = {
        features: features, compare: compare, symKL: symKL, verdict: verdict, quality: quality,
        observations: observations, toMono: toMono, bandwidthHz: bandwidthHz,
        THRESH: THRESH, N_MFCC: N_MFCC
    };
})();
